import { useConsentManager } from '@c15t/react';

import { t } from '../../translations.js';
import { Button } from '../../ui/button.js';

/**
 * Bottom-center bar consent banner.
 *
 * Replaces the default `<ConsentBanner>` from `@c15t/react`. We render directly
 * from `useConsentManager()` rather than via c15t's compound atoms because the
 * layout and button hierarchy we want diverge enough from c15t's defaults that
 * overriding via CSS / theme tokens would be more code than this — and would
 * re-fight c15t's CSS on every minor SDK bump.
 *
 * Hierarchy (per the UX-playground "brand-ghost" preset):
 *   - Accept all: filled brand (NOMA primary)
 *   - Alleen noodzakelijk: ghost — muted text, hover restores
 *   - Voorkeuren: text link
 *
 * Banner is **not** Esc-dismissible: it requests an explicit consent choice
 * and any keyboard-driven escape happens via the preferences dialog (which
 * c15t's `<ConsentDialog>` handles natively).
 */
export function NomaConsentBanner() {
  const { activeUI, setActiveUI, saveConsents } = useConsentManager();

  if (activeUI !== 'banner') return null;

  return (
    <div
      role="region"
      aria-label={t.consent.banner.title}
      data-testid="consent-banner"
      className="fixed inset-x-4 bottom-12 z-40 mx-auto max-w-5xl rounded-lg border border-border bg-background/95 px-5 py-4 shadow-lg backdrop-blur"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <p className="flex-1 text-sm text-muted-foreground">
          {t.consent.banner.description}
        </p>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => void saveConsents('necessary')}
            data-testid="consent-banner-reject-btn"
          >
            {t.consent.banner.rejectAll}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => void saveConsents('all')}
            data-testid="consent-banner-accept-btn"
          >
            {t.consent.banner.acceptAll}
          </Button>
          <Button
            variant="link"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setActiveUI('dialog')}
            data-testid="consent-banner-customize-btn"
          >
            {t.consent.banner.customize}
          </Button>
        </div>
      </div>
    </div>
  );
}
